/**
 * 발화 자막 생성 — 전사 타임코드에서 자막 아이템을 만드는 **순수 함수**.
 *
 * 왜 기계가 만드는가: AI에게 자막 카피를 쓰게 하면 실제 발화에 없는 문장이 들어가고
 * 타이밍이 어긋난다(docs/shorts-editor-quality-redesign.md §2-D). 상용 쇼츠 도구는
 * 전부 자막을 100% 전사로 채운다. 여기서도 자막 텍스트는 전사에서만 나온다.
 *
 * 소유권: `caption-1`은 **기계 소유**다. 컷이 바뀌면 통째로 다시 만들어지므로 사람이
 * 손으로 넣은 문구는 여기 두지 않는다 — 그건 `caption-2`(개입 자막) 몫이다.
 *
 * 좌표계: 입력 초는 **소스(클립) 시간**, 출력 `from`은 **타임라인 프레임**이다.
 * 컷이 있으면 그 매핑이 1:1이 아니므로 반드시 클립 배치를 함께 넘긴다.
 */
import {
  type CaptionStyle,
  type ClipItem,
  type Edl,
  DEFAULT_TRACK_IDS,
  itemEnd,
  secToFrames,
} from './edl';

/** 전사 세그먼트 (초는 클립 시작 기준). */
export interface SpeechSegment {
  start: number;
  end: number;
  text: string;
}

/** 단어 타임코드 (초는 클립 시작 기준). */
export interface SpeechWord {
  start: number;
  end: number;
  word: string;
}

export interface SpeechCaption {
  /** 타임라인 프레임. */
  from: number;
  durationInFrames: number;
  text: string;
}

/**
 * 자막이 화면 좌우로 쓸 수 있는 폭(px @1080).
 *
 * 🔴 **자막은 줄바꿈이 되지 않는다.** 익스포트는 ASS `WrapStyle: 2`(명시적 `\N` 외
 * 줄바꿈 없음)이고 프리뷰(PixiJS `Text`)도 `wordWrap` 을 켜지 않는다. 즉 한 줄이 폭을
 * 넘으면 **줄이 바뀌는 게 아니라 화면 밖으로 잘려 나간다.**
 * 그래서 {@link CAPTION_RULES.maxChars} 는 «읽기 편함» 설정이 아니라 **유일한 안전장치**다.
 *
 * 960 = 캔버스 1080 − ASS MarginL/R 60×2 (`edl-to-ass.ts`).
 */
export const CAPTION_SAFE_WIDTH_PX = 960;

/**
 * 한글 한 글자가 먹는 폭의 추정치(폰트 크기 대비).
 *
 * Pretendard 를 비롯한 한글 폰트의 한글 음절은 advance 가 사실상 1em 이다. 공백·라틴은
 * 더 좁으므로 **한글로만 채운 최악의 줄**을 기준으로 잡는다 — 안전장치는 최악값으로 잰다.
 */
export const HANGUL_ADVANCE_EM = 1.0;

/**
 * 자막 분할 규칙.
 *
 * ⚠️ **옛 근거(Netflix 한국어 가이드 16자)는 폐기했다.** Netflix 기준은 **가로 화면**
 * 것이다. 세로 1080 에 64px 로 14자를 쓰면 14×64 + 외곽선 12 = **908px**, 안전폭 960 의
 * **95%** 다. 줄바꿈이 없는 구조(위 참조)에서 이건 여유가 아니라 벼랑 끝이다.
 * 실무자 지적(키리누키 채널, 구독 4만): "보통 8~10자에서 끊는다. 14자면 눈이 좌우로
 * 왕복한다." → 10자 = 652px = 안전폭의 **68%**. 자막 강조로 크기를 키울 여지도 남는다.
 *
 * 표시 시간도 같이 줄였다(2.2 → 1.5초). "꽉 찬 텍스트가 2초 넘게 서 있으면 정지 화면처럼
 * 느껴진다"(같은 실무자), "말 빠른 구간에서 자막이 말보다 늦게 느껴진다"(시청자).
 * 쇼츠 캡션 관행도 1.2~1.5초다.
 *
 * 근거: `docs/shorts-editor-input-redesign.md` §11.5
 */
export const CAPTION_RULES = {
  /** 이 길이를 넘으면 단어 단위로 쪼갠다. */
  maxDurationSec: 1.5,
  /** 이 글자 수를 넘으면 쪼갠다 (공백 포함). 화면 밖으로 나가는 것을 막는 상한이다. */
  maxChars: 10,
  /**
   * 조각 최소 표시 시간. 이보다 짧아지면 **버리지 않고** 뒤 자막과 병합한다 —
   * 빠른 리액션 구간("아 진짜" "대박" "뭐야")은 짧은 발화가 연속하므로, 짧다고
   * 지우면 그 구간 자막이 통째로 사라진다.
   */
  /**
   * 읽기 속도 하한(초당 글자). 표시 시간이 `글자수 / readingCps` 보다 짧으면 못 읽는다.
   *
   * 5.5자/초 근거(실무자 검수): "한국어 쇼츠 자막 편한 속도는 초당 5자 안팎." 실측에서
   * 9자짜리 **강조** 대사가 1.1초만 떠 있었다 — 키워 놓고 읽을 시간을 안 준 것이다.
   *
   * ⚠️ **보장이 아니라 최선이다.** 자막 시간은 발화 시간을 따르고, 다음 자막이 바로
   * 붙어 있으면 늘릴 여유가 없다. 억지로 늘리면 자막이 말보다 늦어져 더 나쁘다.
   * 그리고 `maxChars`(10) ÷ `readingCps`(5.5) = 1.82초 > `maxDurationSec`(1.5초)라
   * **두 규칙은 최악의 경우 충돌한다** — 그때는 상한이 이긴다. 상한을 넘겨 늘리지 않는다.
   * (실측: 228줄 중 77줄이 이 하한 미달. 대부분 원래 말이 빠른 구간이다.)
   */
  readingCps: 5.5,
  minDurationSec: 0.6,
  /** 조각 사이 간극이 이보다 짧으면 앞 조각을 늘려 깜빡임을 없앤다. */
  bridgeGapSec: 0.25,
  /** 발화보다 이만큼 먼저 띄운다 (읽기 시작 시간 확보). 클립 시작을 넘지 않는다. */
  leadSec: 0.12,
} as const;

/**
 * 자막으로 만들지 않는 발화 — 필러와 단독 감탄사.
 * 화면에 "어", "음"만 뜨는 건 정보가 아니라 노이즈다(totus 원어자막 가이드라인).
 *
 * 🔴 **웃음은 여기 넣지 마라.** 금지목록(`docs/shorts-editor-input-redesign.md` §6-2)이
 * "방송 클립에서 웃음은 콘텐츠 본체"라고 못 박았는데, 예전에는 이 목록에 `하+`·`허+` 가
 * 들어 있어 «하하하»가 필러로 버려졌다. 오디오에서 안 지우기로 한 것을 자막에서 지우고
 * 있었던 셈이다. 실사용자 지적 원문: "«아 진짜 ㅋㅋㅋㅋㅋ»가 박혀야 웃긴 건데
 * «아 진짜»만 남으면 밋밋한 실황 중계가 된다."(§11.2)
 */
const FILLER = /^(어+|음+|아+|에+|그+|저기+|뭐|응+|네+|예+|오+|우+|와+|하|허|헐|[ㅋㅎ])$/;

/**
 * 웃음 — **같은 소리가 두 번 이상 반복된 것**.
 *
 * 반복이 판별 기준인 이유: "하"는 감탄사(필러)지만 "하하하"는 웃음이다. 한국어 전사에서
 * whisper 는 웃음을 `ㅋㅋㅋ` 로도, `하하하` 로도 내놓는다 — 둘 다 잡아야 한다.
 */
const LAUGH = /^(?:[ㅋㅎ]{2,}|(하|허|히|헤|후|흐)\1+)$/;

/**
 * 웃음 자막이 먹어도 되는 최대 글자 수. `ㅋㅋㅋㅋㅋㅋㅋㅋㅋ` 를 그대로 실으면 한 줄
 * 예산(`maxChars`)을 웃음 하나가 다 쓴다. 웃고 있다는 사실은 3~5자면 전달된다.
 */
const LAUGH_MAX_CHARS = 5;

/** 웃음을 앞 자막에 붙일 수 있는 최대 간극(초). 이보다 벌어지면 별개의 비트다. */
const LAUGH_ATTACH_GAP_SEC = 0.6;

/**
 * 자막 한 덩어리가 쓸 수 있는 최대 줄 수.
 *
 * 3줄이 되면 세로 화면에서 영상을 너무 가리고, 하단 앵커라 위로 자라면서 자막이
 * 화면 중앙까지 올라온다. 2줄을 넘길 바에는 두 자막으로 나누는 게 낫다.
 */
export const MAX_CAPTION_LINES = 2;

type PieceKind = 'keep' | 'laugh' | 'drop';

/**
 * 발화 한 덩어리를 «남길 것 / 웃음 / 버릴 것»으로 가른다.
 *
 * 웃음을 먼저 본다 — FILLER 의 `하`·`허` 와 겹치기 때문이다.
 */
function classifySegment(text: string): PieceKind {
  const t = text.normalize('NFC').replace(/[.,!?…~\s]/g, '');
  if (!t) return 'drop';
  if (LAUGH.test(t)) return 'laugh';
  if (FILLER.test(t)) return 'drop';
  return 'keep';
}

/** 반복 음절을 상한까지 줄인다. `ㅋㅋㅋㅋㅋㅋㅋ` → `ㅋㅋㅋㅋㅋ` */
function clampLaugh(text: string): string {
  const t = text.normalize('NFC').replace(/[.,!?…~\s]/g, '');
  return t.length <= LAUGH_MAX_CHARS ? t : t.slice(0, LAUGH_MAX_CHARS);
}

/**
 * 표기 정리 — 들리는 대로 두되 문장 끝 마침표만 없앤다.
 * 쇼츠 자막에서 마침표는 시각적 노이즈이고, 물음표·느낌표·말줄임표는 톤을 담아 남긴다.
 *
 * **NFC 정규화를 먼저 한다.** 자모가 분리된(NFD) 문자열은 `"하하하".length` 가 3 이 아니라
 * 6 으로 세어져 폭 예산과 분할 기준이 동시에 어긋나고, 웃음·필러 판별도 전부 빗나간다.
 *
 * 문장 **안에** 섞인 긴 웃음도 여기서 줄인다. `clampLaugh` 는 세그먼트 전체가 웃음일 때만
 * 도는데, whisper 는 "아 진짜 ㅋㅋㅋㅋㅋㅋㅋㅋㅋㅋ" 처럼 섞어서 내놓는다. 그 `ㅋ` 덩어리는
 * 공백이 없어 한 단어라 단어 단위 분할로도 못 쪼갠다 — 여기서 안 줄이면 화면 밖으로 나간다.
 */
function normalize(text: string): string {
  return text
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[ㅋㅎ]{3,}/g, (run) => run.slice(0, LAUGH_MAX_CHARS))
    .replace(/(하|허|히|헤|후|흐)\1{2,}/g, (run) => run.slice(0, LAUGH_MAX_CHARS))
    .replace(/\.+$/, '');
}

/** 여러 줄 자막에서 가장 긴 줄의 글자 수 — 폭은 줄 단위로 결정된다. */
export function longestLineLength(text: string): number {
  return text.split('\n').reduce((n, line) => Math.max(n, line.length), 0);
}

/**
 * 한 줄이 {@link CAPTION_RULES.maxChars} 를 넘지 않도록 **줄바꿈을 넣는다.**
 *
 * 왜 자르지 않고 줄을 바꾸나: 자막은 자동 줄바꿈이 안 되지만(ASS `WrapStyle: 2`,
 * 프리뷰 `wordWrap` 미설정) **명시적 줄바꿈은 양쪽 다 존중한다**
 * (`edl-to-ass.ts` 가 `\n` → `\N` 으로 변환). 잘라내면 전사가 유실되고, 그냥 두면
 * 화면 밖으로 나간다 — 줄을 바꾸는 것만이 둘 다 피한다.
 *
 * 공백이 있으면 공백에서 끊고, 한 단어가 통째로 길면(URL·긴 웃음) 글자 수로 끊는다.
 */
export function wrapToWidth(text: string, maxChars: number = CAPTION_RULES.maxChars): string {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    let rest = paragraph.trim();
    while (rest.length > maxChars) {
      // maxChars 이내의 마지막 공백에서 끊는다. 없으면 글자 수로 강제로 끊는다.
      const cut = rest.lastIndexOf(' ', maxChars);
      const at = cut > 0 ? cut : maxChars;
      lines.push(rest.slice(0, at).trim());
      rest = rest.slice(at).trim();
    }
    if (rest) lines.push(rest);
  }
  return lines.join('\n');
}

/**
 * 이 어미로 끝나면 **끊어도 되는 자리**로 본다.
 *
 * 🔴 왜 필요한가(2026-08-13 실물 검수). 시간·글자수 규칙은 완벽히 지켰는데 결과가 이랬다:
 * ```
 * 10.33 +1.43 "아 다행이다 내가"
 * 11.77 +0.77 "아는 살인마라"
 * ```
 * 실무자 판정 원문 — "화면에 뜨는 걸 읽으면 무슨 말인지 모른다. 한 문장을 **어절 중간에서**
 * 끊었다. … 19줄 상한 초과보다 이게 더 크다 — 19줄은 눈에 띄어서 그 줄만 고치면 되는데,
 * 이건 전수로 훑어야 하니까." 즉 규칙이 «초와 글자수만» 보니 문장을 아무 데서나 잘랐다.
 *
 * 보수적으로 잡는다. 넓게 잡으면 «내가»(주격 조사 -가)·«아는»(관형형 -는) 같은 문장 **중간**
 * 이 경계로 잡혀 지금보다 더 나빠진다. 조사는 넣지 않고 **종결·연결 어미만** 넣는다.
 */
const CLAUSE_TAIL =
  /(?:[.!?…,]|는데|지만|거든|잖아|구나|더라|니까|아서|어서|면서|는지|던데|길래|는걸|을까|ㄹ까|[다요죠자까네군냐라고며면서])$/;

/** 이 낱말에서 끊어도 말이 되는가. 한 글자짜리는 어미인지 알 수 없어 보지 않는다. */
function isClauseEnd(word: string): boolean {
  const w = word.trim();
  return w.length >= 2 && CLAUSE_TAIL.test(w);
}

/**
 * 세그먼트 하나를 규칙에 맞는 조각들로 쪼갠다 (소스 초).
 *
 * 단어 배열은 세그먼트 범위와 **겹치기만 하면** 채택한다. 경계에 걸친 단어를 버리면
 * 자막 텍스트에서 그 단어가 통째로 사라져 "100% 전사"가 깨진다.
 *
 * 끊는 자리는 **어미 우선**이다({@link CLAUSE_TAIL}). 상한에 딱 닿는 지점이 아니라, 그
 * 앞의 마지막 어미에서 끊는다. 조각이 상한보다 짧아지는 것은 감수한다 — 짧은 건 붙여
 * 읽히지만 문장 중간에서 끊긴 건 다시 읽어야 한다.
 */
function splitSegment(seg: SpeechSegment, words: SpeechWord[]): SpeechSegment[] {
  const text = normalize(seg.text);
  if (!text) return [];
  const dur = seg.end - seg.start;
  if (dur <= CAPTION_RULES.maxDurationSec && text.length <= CAPTION_RULES.maxChars) {
    return [{ start: seg.start, end: seg.end, text }];
  }

  // 겹치는 단어를 모두 취한다(경계 포함). 없으면 쪼갤 근거가 없으므로 **시간은** 통째로
  // 둔다 — 임의로 n등분하면 whisper 정렬 실패와 같은 가짜 타임코드가 된다.
  // 다만 **폭은 그대로 둘 수 없다.** 자막은 자동 줄바꿈이 안 돼서, 긴 문장을 한 줄로 두면
  // 좌우가 화면 밖으로 잘려 나간다(옛 동작: 38자 = 2432px, 캔버스 1080). 줄바꿈을 넣는다.
  const inSeg = words.filter((w) => w.end > seg.start + 0.01 && w.start < seg.end - 0.01);
  if (inSeg.length === 0) return [{ start: seg.start, end: seg.end, text: wrapToWidth(text) }];

  const out: SpeechSegment[] = [];
  let cur: SpeechWord[] = [];
  /** `cur` 의 앞 `count` 낱말만 내보내고 나머지는 남긴다. count 를 안 주면 전부. */
  const flush = (count = cur.length) => {
    const take = cur.slice(0, count);
    if (take.length === 0) return;
    // 한 단어가 통째로 상한을 넘는 경우(긴 웃음·URL)가 있어 마지막에 한 번 더 감싼다.
    const t = wrapToWidth(normalize(take.map((w) => w.word).join(' ')));
    if (t) out.push({ start: take[0]!.start, end: take[take.length - 1]!.end, text: t });
    cur = cur.slice(count);
  };
  for (const w of inSeg) {
    const nextChars = cur.reduce((n, x) => n + x.word.length + 1, 0) + w.word.length;
    const nextDur = cur.length ? w.end - cur[0]!.start : w.end - w.start;
    if (
      cur.length > 0 &&
      (nextChars > CAPTION_RULES.maxChars || nextDur > CAPTION_RULES.maxDurationSec)
    ) {
      /*
       * 어디서 끊을지 고른다. 기본은 «지금까지 모은 전부»지만, 마지막 낱말이 어미가
       * 아니면 그 앞의 마지막 어미까지만 끊는다 — 남는 낱말은 다음 조각의 머리가 된다.
       *
       * `i < cur.length - 1` 조건이 핵심이다. 마지막 낱말이 이미 어미면 통째 flush 가
       * 곧 어미 정렬이므로 아무것도 바꾸지 않는다.
       */
      let cut = cur.length;
      if (!isClauseEnd(cur[cur.length - 1]!.word)) {
        for (let i = cur.length - 2; i >= 0; i--) {
          if (!isClauseEnd(cur[i]!.word)) continue;
          /*
           * 남길 낱말들 + 지금 낱말이 상한 안에 들어올 때만 어미 자리로 물러선다.
           * 이 가드가 없으면 «다음 조각의 머리»가 자라서 상한을 넘긴 채 시작한다 —
           * 규칙을 지키려다 규칙을 깨는 꼴이다.
           */
          const carried = cur.slice(i + 1);
          const carriedChars = carried.reduce((n, x) => n + x.word.length + 1, 0) + w.word.length;
          if (carriedChars <= CAPTION_RULES.maxChars) cut = i + 1;
          break;
        }
      }
      flush(cut);
    }
    cur.push(w);
  }
  flush();
  return out.length ? out : [{ start: seg.start, end: seg.end, text: wrapToWidth(text) }];
}

/** 분할된 조각. `laugh` 는 «앞 말에 붙일 수 있는 웃음»이라는 표시다. */
interface Piece extends SpeechSegment {
  laugh?: boolean;
}

/** 타임라인 위 클립 하나가 커버하는 소스 구간. */
interface Placement {
  index: number;
  clip: ClipItem;
  inSec: number;
  outSec: number;
  /**
   * **장면** 번호 — «자막이 넘어가도 되는 범위».
   *
   * 🔴 클립 번호가 아니다. 펀치 줌은 컷 하나를 세 조각으로 쪼개지만 그건 **컷이 아니라
   * 확대**다. 화면이 튀지 않으므로 자막이 그 경계를 넘어도 아무 문제가 없다. 그런데
   * 클립 단위로 막았더니 실측에서 `이제`(0.4초)가 줌 조각 끝에 갇혀 하한(0.6초)까지
   * 늘지도, 뒤 `갑니다`와 합쳐지지도 못했다. 실무자 판정은 «눈에 안 들어온다»였다.
   *
   * 그래서 «같은 소스가 끊김 없이 이어지는» 연속 클립을 한 장면으로 묶는다. 진짜 컷
   * (다른 소스·점프)은 소스가 안 이어지므로 여전히 갈린다 — 2라운드 리뷰 blocker
   * (자막이 컷을 넘어 앞 꼬리에 붙어 사라지던 것)는 그대로 막힌 채다.
   */
  sceneIndex: number;
  /** 이 장면이 끝나는 타임라인 프레임 (연장 상한). */
  sceneEnd: number;
}

/**
 * 자막 + 소속 장면. 장면 식별자는 **정리 단계에서만** 쓰는 내부 정보다 —
 * 병합·연장을 장면 안에서만 하려면 어느 장면 소속인지 알아야 한다.
 */
interface PlacedCaption extends SpeechCaption {
  sceneIndex: number;
  /** 앞 자막에 붙일 수 있는 웃음인가 (`tidy` 에서만 쓴다). */
  laugh?: boolean;
  /** 이 장면이 끝나는 타임라인 프레임 (연장 상한). */
  sceneEnd: number;
}

/**
 * 조각이 이 클립을 "주로" 차지한다고 볼 최소 겹침 비율.
 * 컷 경계에 걸친 발화를 양쪽에 넣으면 같은 문장이 두 번 뜨므로, 더 많이 겹치는
 * 쪽에만 둔다. 같은 소스 구간을 두 번 쓴 경우(반복 사용)는 양쪽 다 1.0이라 둘 다 남는다.
 */
const DOMINANT_OVERLAP = 0.6;

/**
 * 발화 조각을 클립 배치에 투영한다.
 *
 * 조각이 여러 클립에 걸치면 **가장 많이 겹치는 클립**에만 둔다(같은 자막 중복 방지).
 * 클립 안쪽으로 클램프하므로 자막이 컷을 넘어 흐르지 않는다.
 */
function placePiece(piece: Piece, placements: Placement[]): PlacedCaption[] {
  const pieceDur = piece.end - piece.start;
  const hits: { p: Placement; s: number; e: number; ratio: number }[] = [];
  for (const p of placements) {
    const s = Math.max(piece.start, p.inSec);
    const e = Math.min(piece.end, p.outSec);
    if (e - s <= 0.01) continue;
    hits.push({ p, s, e, ratio: pieceDur > 0 ? (e - s) / pieceDur : 1 });
  }
  if (hits.length === 0) return [];

  // 지배적으로 겹치는 클립들만. 아무것도 기준을 못 넘으면 최대 겹침 하나만 남긴다
  // (경계에 반반 걸친 발화가 양쪽에 중복으로 뜨는 것을 막는다).
  let chosen = hits.filter((h) => h.ratio >= DOMINANT_OVERLAP);
  if (chosen.length === 0) {
    chosen = [hits.reduce((a, b) => (b.ratio > a.ratio ? b : a))];
  }

  const out: PlacedCaption[] = [];
  for (const { p, s, e } of chosen) {
    // lead 는 **클립 안에서만** 당긴다 — 소스 시간에 적용하면 앞 클립으로 넘어가
    // 자막이 엉뚱한 컷 위에 붙는다.
    const leadSec = Math.min(CAPTION_RULES.leadSec, Math.max(0, s - p.inSec));
    const startFrame = p.clip.from + secToFrames(s - leadSec - p.inSec);
    const endFrame = p.clip.from + secToFrames(e - p.inSec);
    const lo = Math.max(p.clip.from, startFrame);
    const hi = Math.min(p.sceneEnd, Math.max(endFrame, lo + 1));
    if (hi <= lo) continue;
    out.push({
      from: lo,
      durationInFrames: hi - lo,
      text: piece.text,
      sceneIndex: p.sceneIndex,
      sceneEnd: p.sceneEnd,
      ...(piece.laugh ? { laugh: true as const } : {}),
    });
  }
  return out;
}

/**
 * 자막 자기검사 결과 — **규칙이 결과를 몇 번 고쳤나.**
 *
 * 🔴 왜 «0 위반»만으로는 부족한가(2026-08-13 실무자 검수). 원문 —
 * "검사기가 «고치기»까지 하면서 **흔적을 안 남긴다.** 상한 초과 0줄이 «원래 없어서 0»인지
 *  «4.7초를 1.5초로 잘라서 0»인지 결과만 봐서는 구분이 안 된다. whisper 가 침묵을 묶은
 *  케이스라 잃는 말이 없다는 설명은 이번 클립엔 맞겠지만, **그게 항상 참이라고 믿을 근거는
 *  없다.** 몇 줄을 어떻게 고쳤다는 카운트만 남겨줘라. **0 이라는 숫자보다 그게 더 쓸모 있다.**"
 */
export interface CaptionAudit {
  /** 최종 자막 줄 수. */
  total: number;
  /** 표시시간 상한을 넘어 **줄인** 줄 수. */
  clamped: number;
  /** 하한·읽기속도에 못 미쳐 **늘린** 줄 수. */
  extended: number;
  /** 다른 줄과 **합친** 횟수 (앞으로·뒤로 모두). */
  merged: number;
  /** 화면 밖으로 나가지 않게 **줄바꿈을 넣은** 줄 수. */
  wrapped: number;
  /**
   * 고친 뒤에도 읽기 속도({@link CAPTION_RULES.readingCps}) 하한에 **못 미치는** 줄 수.
   * 늘릴 여백이 없었다는 뜻이다 — 규칙 위반이 아니라 «최선을 다한 결과»다.
   */
  shortReading: number;
  /**
   * 그중 **끝 {@link NEAR_END_SEC}초 안**에 있는 줄 수.
   *
   * 실무자가 비율을 보지 말라고 한 지점이다 — "38%라는 비율은 의미 없다. 문제는 **위치**다.
   * 클립 마지막 2~3초, **펀치라인에 걸린 자막**이 0.6초로 지나가면 그건 조회수를 깎는다.
   * 비율 말고 «미달 줄이 클립 끝 3초 안에 있는가»만 따로 보여주면 나는 그것만 본다."
   */
  shortReadingNearEnd: number;
}

/** «끝부분»의 정의(초). 실무자가 말한 "마지막 2~3초"의 넉넉한 쪽. */
export const NEAR_END_SEC = 3;

const emptyAudit = (): CaptionAudit => ({
  total: 0,
  clamped: 0,
  extended: 0,
  merged: 0,
  wrapped: 0,
  shortReading: 0,
  shortReadingNearEnd: 0,
});

/**
 * 겹침 제거 + 짧은 간극 메우기 + 최소 길이 확보.
 *
 * 짧은 자막을 **버리지 않는다.** 최소 길이에 못 미치면 뒤 자막과 텍스트를 합쳐
 * 한 덩어리로 보여준다 — 빠른 발화 구간을 통째로 잃지 않기 위해서다.
 *
 * `audit` 에 «몇 번 고쳤나»를 적는다. 고친 흔적을 안 남기면 «0 위반»이 «원래 깨끗했다»와
 * 구분되지 않는다({@link CaptionAudit}).
 */
function tidy(caps: PlacedCaption[], audit: CaptionAudit): SpeechCaption[] {
  const minFrames = secToFrames(CAPTION_RULES.minDurationSec);
  const bridge = secToFrames(CAPTION_RULES.bridgeGapSec);
  const laughGap = secToFrames(LAUGH_ATTACH_GAP_SEC);
  const sorted = [...caps].sort(
    (a, b) => a.from - b.from || a.durationInFrames - b.durationInFrames
  );

  const out: PlacedCaption[] = [];
  for (const cap of sorted) {
    const prev = out[out.length - 1];
    // 장면이 다르면 앞 자막과 **어떤 관계도 맺지 않는다.** 병합·브리지 연장이 컷을
    // 넘어가면 뒤 컷의 첫 자막이 앞 컷 꼬리에 붙어 사라진다(2라운드 리뷰 blocker).
    // 줌으로 쪼개진 조각들은 같은 장면이라 여기서 안 갈린다 — 화면이 안 튀니 넘어가도 된다.
    if (!prev || prev.sceneIndex !== cap.sceneIndex) {
      out.push({ ...cap });
      continue;
    }
    const prevEnd = prev.from + prev.durationInFrames;

    /** 앞 자막에 합치고 길이를 그 끝까지 늘린다. */
    const mergeIntoPrev = (text: string) => {
      audit.merged++;
      prev.text = text;
      prev.durationInFrames = Math.min(
        cap.from + cap.durationInFrames - prev.from,
        prev.sceneEnd - prev.from
      );
    };

    // 웃음은 **앞 말에 붙인다** — "아 진짜 ㅋㅋㅋ" 한 덩어리가 목표다(§6-2).
    // 여기서 하는 이유: `tidy` 만 조각이 어느 컷에 속하는지 안다. 앞 단계에서 붙이면
    // 컷을 넘어 붙어 앞 컷의 자막이 사라진다(리뷰 🔴1).
    //
    // 웃음끼리는 붙이지 않는다 — `ㅋㅋㅋ ㅋㅋㅋ ㅋㅋㅋ` 가 되어 웃음 상한이 무의미해진다.
    if (cap.laugh && !prev.laugh && cap.from - prevEnd <= laughGap) {
      const merged = wrapToWidth(`${prev.text} ${cap.text}`.trim());
      if (merged.split('\n').length <= MAX_CAPTION_LINES) {
        mergeIntoPrev(merged);
        continue;
      }
    }

    // 같은 장면 안에서 앞 자막이 너무 짧으면 병합한다 (텍스트 보존).
    // 단 **웃음끼리는 예외** — 짧다는 이유로 합치면 `ㅋㅋㅋ ㅋㅋㅋ` 가 되어 웃음 상한
    // (LAUGH_MAX_CHARS)이 무의미해진다.
    if (
      prev.durationInFrames < minFrames &&
      cap.from - prevEnd <= bridge &&
      !(cap.laugh && prev.laugh)
    ) {
      const merged = wrapToWidth(`${prev.text} ${cap.text}`.trim());
      // 병합해도 **줄 수**가 상한 안일 때만. 예전엔 `maxChars * 2` 로 «글자 수»를 쟀는데,
      // 자막은 자동 줄바꿈이 안 되므로 20자 한 줄은 1292px 로 캔버스(1080)를 넘어갔다.
      if (merged.split('\n').length <= MAX_CAPTION_LINES) {
        mergeIntoPrev(merged);
        continue;
      }
    }
    if (cap.from < prevEnd) {
      // 겹침 — 앞 자막을 잘라 붙인다. 잘라서 0 이하가 되면 이 조각을 건너뛴다.
      const trimmed = cap.from - prev.from;
      if (trimmed <= 0) continue;
      prev.durationInFrames = trimmed;
    } else if (cap.from - prevEnd <= bridge) {
      prev.durationInFrames = cap.from - prev.from; // 깜빡임 방지
    }
    out.push({ ...cap });
  }

  /*
   * 🔴 **뒤와도 병합한다.**
   *
   * 위 병합은 «앞 자막이 짧을 때»만 본다. 그래서 `… 와라`(1.1초) 뒤에 오는 `이제`(0.4초)
   * 처럼 **자기가 짧은데 앞이 멀쩡한** 조각이 그대로 남았다. 실측에서 0.4초짜리 자막이
   * 나왔고, 실무자 판정은 "눈에 안 들어온다. 실무 최소 0.7초. 뒤 «갑니다»와 붙여서
   * `이제 갑니다` 한 줄이 맞다"였다.
   */
  for (let i = 0; i < out.length - 1; i++) {
    const cur = out[i]!;
    const next = out[i + 1]!;
    if (cur.sceneIndex !== next.sceneIndex) continue;
    // 웃음끼리는 붙이지 않는다 — `ㅋㅋㅋ ㅋㅋㅋ` 가 되어 웃음 상한이 무의미해진다.
    if (cur.laugh && next.laugh) continue;
    if (cur.durationInFrames >= minFrames) continue;
    if (next.from - (cur.from + cur.durationInFrames) > bridge) continue;
    const merged = wrapToWidth(`${cur.text} ${next.text}`.trim());
    if (merged.split('\n').length > MAX_CAPTION_LINES) continue;
    audit.merged++;
    cur.text = merged;
    cur.durationInFrames = Math.min(
      next.from + next.durationInFrames - cur.from,
      cur.sceneEnd - cur.from
    );
    out.splice(i + 1, 1);
    i--;
  }

  // 최소 길이 확보: 병합되지 못한 짧은 자막은 **버리지 않고** 뒤 여백으로 늘린다.
  // 상한은 (같은 장면의) 다음 자막 시작과 자기 장면 끝. 장면이 다르면 다음 자막은
  // 상한이 될 수 없다 — 그건 이미 다른 컷이고 사이에 경계가 있다.
  for (let i = 0; i < out.length; i++) {
    const c = out[i]!;
    if (c.durationInFrames >= minFrames) continue;
    const next = out[i + 1];
    const nextFrom =
      next && next.sceneIndex === c.sceneIndex ? next.from : Number.POSITIVE_INFINITY;
    const room = Math.min(nextFrom, c.sceneEnd) - c.from;
    const grown = Math.max(c.durationInFrames, Math.min(minFrames, room));
    if (grown > c.durationInFrames) audit.extended++;
    c.durationInFrames = grown;
  }

  /*
   * 🔴 **정한 규칙을 결과에 대조한다.**
   *
   * 실측(스테이징 실데이터 235줄)에서 **19줄(8.1%)이 표시시간 상한을 넘었다.** 최장 4.7초 —
   * 상한의 3배다. 실무자 지적: "규칙은 있는데 그 규칙을 **검사하는 게 없다는 뜻**이고,
   * 그러면 다음에 규칙이 하나 더 생겨도 똑같이 새어나간다."
   *
   *  - 너무 길면 **줄인다.** 4.7초짜리 «걸렸다»는 whisper 가 긴 침묵을 한 세그먼트로 묶은
   *    것이라, 텍스트가 짧은데 시간만 길다. 줄여도 잃는 말이 없다.
   *  - 너무 짧으면(읽기 속도 미달) **늘린다.** 다음 자막 시작과 클립 끝이 상한이다.
   */
  const maxFrames = secToFrames(CAPTION_RULES.maxDurationSec);
  for (let i = 0; i < out.length; i++) {
    const c = out[i]!;
    if (c.durationInFrames > maxFrames) {
      audit.clamped++;
      c.durationInFrames = maxFrames;
    }
    const needFrames = secToFrames(longestLineLength(c.text) / CAPTION_RULES.readingCps);
    if (c.durationInFrames < needFrames) {
      const next = out[i + 1];
      const nextFrom =
        next && next.sceneIndex === c.sceneIndex ? next.from : Number.POSITIVE_INFINITY;
      const room = Math.min(nextFrom, c.sceneEnd) - c.from;
      // 상한을 넘겨서까지 늘리지는 않는다 — 두 규칙이 싸우면 상한이 이긴다.
      const grown = Math.max(c.durationInFrames, Math.min(needFrames, room, maxFrames));
      if (grown > c.durationInFrames) audit.extended++;
      c.durationInFrames = grown;
    }
  }

  // 자기 장면 밖으로 나가지 않도록 최종 클램프.
  for (const c of out) {
    if (c.from + c.durationInFrames > c.sceneEnd) {
      c.durationInFrames = Math.max(1, c.sceneEnd - c.from);
    }
  }

  const kept = out.filter((c) => c.durationInFrames > 0);

  /*
   * 마지막 집계. **고친 뒤에 남은 것**을 센다 — 여기서 세지 않고 중간에 세면
   * "늘리려다 못 늘린" 줄과 "늘려서 해결된" 줄이 섞인다.
   *
   * 끝부분 판정 기준은 타임라인 끝(= 가장 늦은 장면 끝)이다. EDL 전체 길이를 따로 받지
   * 않는 이유: 자막 뒤에 영상이 더 있을 수는 없다(자막은 클립 안에만 놓인다).
   */
  const timelineEnd = kept.reduce((m, c) => Math.max(m, c.sceneEnd), 0);
  const nearEndFrom = timelineEnd - secToFrames(NEAR_END_SEC);
  audit.total = kept.length;
  for (const c of kept) {
    if (c.text.includes('\n')) audit.wrapped++;
    const need = secToFrames(longestLineLength(c.text) / CAPTION_RULES.readingCps);
    if (c.durationInFrames < need) {
      audit.shortReading++;
      // «끝 3초 안»은 자막이 그 구간에 **걸치기만** 해도 해당이다. 펀치라인은 대개
      // 마지막 자막이고, 시작 시각만 보면 3.1초 전에 시작한 줄이 빠진다.
      if (c.from + c.durationInFrames > nearEndFrom) audit.shortReadingNearEnd++;
    }
  }

  return kept.map(({ from, durationInFrames, text }) => ({ from, durationInFrames, text }));
}

/** 자막 + **자기검사 결과**. */
export interface CaptionBuildResult {
  captions: SpeechCaption[];
  audit: CaptionAudit;
}

/**
 * 발화 자막을 만든다. 자기검사 결과는 버린다 — 대부분의 호출부가 자막만 필요하다.
 *
 * @param segments 전사 세그먼트 (소스 초)
 * @param words 단어 타임코드 (소스 초). 비어 있으면 세그먼트를 쪼개지 않는다.
 * @param clips 타임라인의 video 클립들 — 소스 초를 타임라인 프레임으로 옮기는 근거.
 *              컷을 바꾼 뒤 다시 호출하면 새 배치에 맞는 자막이 나온다.
 */
export function buildSpeechCaptions(
  segments: SpeechSegment[],
  words: SpeechWord[],
  clips: ClipItem[]
): SpeechCaption[] {
  return buildSpeechCaptionsDetailed(segments, words, clips).captions;
}

/**
 * 자막 + **몇 번 고쳤나**를 함께 돌려준다.
 *
 * 왜 따로 두나: «0 위반»은 «원래 깨끗했다»와 «고쳐서 0이 됐다»를 구분하지 못한다
 * ({@link CaptionAudit}). 그런데 대부분의 호출부는 그 구분이 필요 없어서, 기본
 * ({@link buildSpeechCaptions})은 자막만 준다.
 */
export function buildSpeechCaptionsDetailed(
  segments: SpeechSegment[],
  words: SpeechWord[],
  clips: ClipItem[]
): CaptionBuildResult {
  const audit = emptyAudit();
  if (segments.length === 0 || clips.length === 0) return { captions: [], audit };

  const ordered = clips
    .filter((clip) => clip.source.sourceOutSec > clip.source.sourceInSec)
    .sort((a, b) => a.from - b.from);
  if (ordered.length === 0) return { captions: [], audit };

  /*
   * 장면 나누기 — «화면이 튀는 자리»만 경계로 삼는다.
   *
   * 이어짐의 조건 두 가지를 **모두** 만족해야 한 장면이다:
   *  1. 타임라인이 붙어 있다 (앞 클립 끝 == 뒤 클립 시작)
   *  2. 같은 소스가 끊김 없이 이어진다 (같은 assetId, 앞 sourceOut == 뒤 sourceIn)
   *
   * 크롭(줌)은 보지 않는다 — 확대는 컷이 아니다. 반대로 점프컷·다른 클립은 2번에서
   * 걸리므로 자막이 그 경계를 넘지 못한다.
   */
  const EPS_SEC = 0.02;
  const sceneOf: number[] = [];
  let scene = 0;
  for (let i = 0; i < ordered.length; i++) {
    if (i > 0) {
      const prev = ordered[i - 1]!;
      const cur = ordered[i]!;
      const contiguous =
        itemEnd(prev) === cur.from &&
        prev.source.assetId === cur.source.assetId &&
        Math.abs(prev.source.sourceOutSec - cur.source.sourceInSec) < EPS_SEC;
      if (!contiguous) scene++;
    }
    sceneOf.push(scene);
  }
  /** 장면별 끝 프레임 — 마지막 클립의 끝. */
  const sceneEnds = new Map<number, number>();
  for (let i = 0; i < ordered.length; i++) {
    const s = sceneOf[i]!;
    sceneEnds.set(s, Math.max(sceneEnds.get(s) ?? 0, itemEnd(ordered[i]!)));
  }

  const placements: Placement[] = ordered.map((clip, index) => ({
    index,
    clip,
    inSec: clip.source.sourceInSec,
    outSec: clip.source.sourceOutSec,
    sceneIndex: sceneOf[index]!,
    sceneEnd: sceneEnds.get(sceneOf[index]!)!,
  }));

  const pieces: Piece[] = [];
  for (const seg of segments) {
    if (!Number.isFinite(seg.start) || !Number.isFinite(seg.end) || seg.end <= seg.start) continue;
    const kind = classifySegment(seg.text);
    if (kind === 'drop') continue;
    if (kind === 'laugh') {
      // 웃음은 **버리지 않는다**(§6-2). 다만 여기서 앞 조각에 붙이면 안 된다 —
      // `pieces` 는 소스 시간 배열이고 클립 배치는 아직 안 일어났다. 컷 경계를 사이에
      // 둔 두 조각을 여기서 합치면 앞 컷의 자막이 통째로 뒤 컷으로 넘어간다(리뷰 🔴1).
      // 붙이는 것은 클립을 아는 `tidy` 가 한다.
      const laugh = clampLaugh(seg.text);
      if (!laugh) continue;
      pieces.push({ start: seg.start, end: seg.end, text: laugh, laugh: true });
      continue;
    }
    pieces.push(...splitSegment(seg, words));
  }
  pieces.sort((a, b) => a.start - b.start);

  return {
    captions: tidy(
      pieces.flatMap((p) => placePiece(p, placements)),
      audit
    ),
    audit,
  };
}

/** 발화 자막 기본 스타일 — 하단, 굵은 흰색 + 검정 외곽선. */
export const SPEECH_CAPTION_STYLE: Partial<CaptionStyle> = {
  fontSizePx: 64,
  bold: true,
  primaryColor: '#FFFFFF',
  outlineColor: '#000000',
  outlineWidth: 6,
};

/** EDL의 video 트랙 클립들 (타임라인 순). */
export function videoClipsOf(edl: Edl): ClipItem[] {
  return edl.tracks
    .filter((t) => t.kind === 'video')
    .flatMap((t) => t.items)
    .filter((i): i is ClipItem => i.type === 'clip')
    .sort((a, b) => a.from - b.from);
}

/** `caption-1`에 자막이 하나라도 있으면 true. */
export function hasSpeechCaptions(edl: Edl): boolean {
  const track = edl.tracks.find((t) => t.id === DEFAULT_TRACK_IDS.caption);
  return (track?.items.length ?? 0) > 0;
}
